import { useLanguage } from "@/LanguageContext";
import { MapPin, Mail, Phone, Instagram } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_senior-care-valais/artifacts/8pnba102_image.png";

export default function Footer({ onNavigate }) {
  const { t } = useLanguage();

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const navItems = [
    { id: "services", label: t("nav.services") },
    { id: "method", label: t("nav.method") },
    { id: "values", label: t("nav.values") },
    { id: "testimonials", label: t("nav.testimonials") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <footer
      data-testid="main-footer"
      className="bg-[#2C3E50] text-white py-16 lg:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
              src={LOGO_URL}
              alt="Présence & Sourire"
              className="h-16 w-16 rounded-full object-cover"
            />
              <span className="font-heading text-lg font-semibold">
                Présence & Sourire
              </span>
            </div>
            <p className="font-body text-sm text-white/60 leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-body font-medium text-sm uppercase tracking-[0.15em] text-white/40 mb-5">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollTo(item.id)}
                    className="font-body text-sm text-white/60 hover:text-white transition-colors"
                    data-testid={`footer-link-${item.id}`}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-body font-medium text-sm uppercase tracking-[0.15em] text-white/40 mb-5">
              {t("footer.contactTitle")}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <Phone size={14} className="text-[#6FA8B6] shrink-0" strokeWidth={1.5} />
                <a
                  href="tel:+41783293318"
                  className="font-body text-sm text-white/60 hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  +41 78 329 33 18
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-[#6FA8B6] shrink-0" strokeWidth={1.5} />
                <a
                  href="mailto:info_presenceetsourire@etik.com"
                  className="font-body text-sm text-white/60 hover:text-white transition-colors break-all"
                  data-testid="footer-email"
                >
                  info_presenceetsourire@etik.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={14} className="text-[#6FA8B6] shrink-0" strokeWidth={1.5} />
                <span className="font-body text-sm text-white/60">
                  {t("footer.location")}
                </span>
              </li>
            </ul>
          </div>

          {/* Follow */}
          <div>
            <h4 className="font-body font-medium text-sm uppercase tracking-[0.15em] text-white/40 mb-5">
              {t("footer.followUs")}
            </h4>
            <a
              href="https://www.instagram.com/presence_sourire"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-body text-sm text-white/60 hover:text-white transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={16} strokeWidth={1.5} />
              @presence_sourire
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="font-body text-xs text-white/40">
            &copy; {new Date().getFullYear()} Présence & Sourire. {t("footer.rights")}
          </p>
          <div className="flex gap-6">
            <button onClick={() => onNavigate?.("legal")} className="font-body text-xs text-white/40 hover:text-white/60 transition-colors" data-testid="footer-legal">
              {t("footer.legal")}
            </button>
            <button onClick={() => onNavigate?.("privacy")} className="font-body text-xs text-white/40 hover:text-white/60 transition-colors" data-testid="footer-privacy">
              {t("footer.privacy")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
